import liveServerStatus from '../../liveServerStatus';

/**
 * Generates HTML email template for document upload notifications
 * @param {Object} employee - Employee information including name
 * @param {Object} documentData - Information about the uploaded document
 * @returns {string} - HTML formatted email
 */
const generateDocumentUploadEmailHTML = (employee, documentData) => {
  // Get first name for greeting
  const firstName = employee.firstName || employee.name?.split(' ')[0] || 'there';
  
  // Document category color
  const categoryColors = {
    'ID & Visa': '#51796F',
    'Employment Document': '#6E7C7C',
    'Miscellaneous': '#E57373'
  };
  
  const categoryColor = categoryColors[documentData.category] || '#0077CC';
  
  // Format the document type for display
  const formatFileType = (fileType) => {
    switch(fileType.toLowerCase()) {
      case 'pdf': return 'PDF Document';
      case 'jpg': 
      case 'jpeg': 
      case 'png': 
      case 'gif': return 'Image File';
      case 'doc': 
      case 'docx': return 'Word Document';
      default: return 'Document';
    }
  };
  
  // Add test environment indicator in the header if not in live environment
  const testEnvironmentBanner = !liveServerStatus ? `
    <div style="background-color: #ffeb3b; color: #333; text-align: center; padding: 5px; font-weight: bold; font-size: 12px;">
      ⚠️ TEST EMAIL - No action required ⚠️
    </div>
  ` : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>New Document Available</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f5f5f5;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        }
        .email-header {
          background-color: ${categoryColor};
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        .email-body {
          padding: 30px;
        }
        .greeting {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 20px;
          color: #212121;
        }
        .document-details {
          background-color: #f5f5f5;
          border-radius: 6px;
          padding: 20px;
          margin-bottom: 25px;
          border: 1px solid #e0e0e0;
        }
        .document-title {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 12px;
          color: #212121;
        }
        .document-info {
          margin-bottom: 8px;
          display: flex;
        }
        .document-info-label {
          font-weight: 500;
          color: #666;
          width: 120px;
        }
        .document-info-value {
          flex: 1;
        }
        .category-tag {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 16px;
          background-color: ${categoryColor}; 
          color: white;
          font-size: 12px;
          font-weight: 500;
        }
        .divider {
          height: 1px;
          background-color: #e0e0e0;
          margin: 25px 0;
        }
        .cta-button {
          display: inline-block;
          background-color: ${categoryColor};
          color: white;
          text-decoration: none;
          padding: 12px 25px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 5px;
          text-align: center;
        }
        .message {
          margin-bottom: 25px;
          color: #424242;
        }
        .email-footer {
          background-color: #212121;
          padding: 20px;
          text-align: center;
          color: #f5f5f5;
          font-size: 12px;
        }
        .company-name {
          font-weight: 600;
          color: #f5f5f5;
          margin-bottom: 8px;
        }
      </style>
    </head>
    <body>
      ${testEnvironmentBanner}
      <div class="email-container">
        <div class="email-header">
          <h1>New Document Available</h1>
        </div>
        
        <div class="email-body">
          <div class="greeting">
            Hello ${firstName},
          </div>
          
          <div class="message">
            A new document has been uploaded to your profile. You can access this document in the "My Documents" section of the portal.
          </div>
          
          <div class="document-details">
            <div class="document-title">Document Details</div>
            
            <div class="document-info">
              <div class="document-info-label">Category:</div>
              <div class="document-info-value">
                <span class="category-tag">${documentData.category}</span>
              </div>
            </div>
            
            <div class="document-info">
              <div class="document-info-label">File Name:</div>
              <div class="document-info-value">${documentData.fileRefName}</div>
            </div>
            
            <div class="document-info">
              <div class="document-info-label">File Type:</div>
              <div class="document-info-value">${formatFileType(documentData.fileType)}</div>
            </div>
            
            <div class="document-info">
              <div class="document-info-label">Uploaded On:</div>
              <div class="document-info-value">${new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</div>
            </div>
          </div>
          
          <a href="${documentData.fileURL}" class="cta-button">View Document</a>
          
          <div class="divider"></div>
          
          <div class="message">
            If you have any questions or need assistance accessing this document, please contact your administrator.
          </div>
        </div>
        
        <div class="email-footer">
          <div class="company-name">MyelinZ PPC Portal</div>
          <p>This is an automated message. Please do not reply directly to this email.</p>
          <p>© ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
          ${!liveServerStatus ? '<p><strong>TEST ENVIRONMENT - No action required</strong></p>' : ''}
        </div>
      </div>
    </body>
    </html>
  `;
};

export default generateDocumentUploadEmailHTML; 